import { MessageList, Window, useChannelStateContext } from "stream-chat-react";
import { useMemo, useState } from "react";
import { ChatChannelHeader } from "Components/Chat/ChatChannelHeader";
import { ChatFindHighlightProvider } from "Components/Chat/ChatFindHighlightContext";
import { ChatComposer } from "Components/Chat/ChatComposer";
import { ChatRequestActionBar } from "Components/Chat/ChatRequestActionBar";
import {
  IS_MANUAL_THREADING_ENABLED,
  MESSAGE_COMPOSER_MAX_ROWS,
} from "Components/Chat/chatConstants";
import {
  MESSAGE_ACTIONS_WITH_THREADS,
  MESSAGE_ACTIONS_WITHOUT_THREADS,
} from "Components/Chat/chatMessageActions";
import { ChatPaymentThread } from "Components/Chat/ChatPaymentThread";
import { useChatThreadUrlSync } from "Hooks/Chat/useChatThreadUrlSync";

type Props = {
  isMobile: boolean;
  onBack?: () => void;
};

// The edit form is a MessageInput of Stream's own making, configured through the list that hosts
// the message. Same growth limit as ChatComposer.
const MESSAGE_INPUT_PROPS = { maxRows: MESSAGE_COMPOSER_MAX_ROWS };

/**
 * The inside of `<Channel>`: conversation window + thread.
 *
 * Stream lays the two out side by side and has no responsive handling of its own, so on a phone
 * an open thread used to split the screen 50/50 with the conversation. On mobile the thread is a
 * page on the stack instead: the conversation UNMOUNTS while a thread is open (the message list
 * remounts scrolled to the bottom on return, and the composer draft survives in
 * `channel.messageComposer`). Desktop keeps the side-by-side layout.
 */
export const ChatChannelView = ({ isMobile, onBack }: Props) => {
  useChatThreadUrlSync();
  const { thread } = useChannelStateContext();

  const isThreadPage = isMobile && !!thread;

  // Current "Find in conversation" match; lives here because the bar (in the header) sets it and
  // the messages (in the list) read it.
  const [findHighlightedMessageId, setFindHighlightedMessageId] = useState<
    string | null
  >(null);
  const findHighlightValue = useMemo(
    () => ({
      highlightedMessageId: findHighlightedMessageId,
      setHighlightedMessageId: setFindHighlightedMessageId,
    }),
    [findHighlightedMessageId],
  );

  return (
    <ChatFindHighlightProvider value={findHighlightValue}>
      {!isThreadPage && (
        <Window>
          <ChatChannelHeader onBack={onBack} />
          <MessageList
            additionalMessageInputProps={MESSAGE_INPUT_PROPS}
            messageActions={
              IS_MANUAL_THREADING_ENABLED
                ? MESSAGE_ACTIONS_WITH_THREADS
                : MESSAGE_ACTIONS_WITHOUT_THREADS
            }
          />
          <ChatRequestActionBar />
          <ChatComposer />
        </Window>
      )}
      <ChatPaymentThread />
    </ChatFindHighlightProvider>
  );
};
